import React, { useState } from 'react';
import { Check, User } from 'lucide-react';
import { useAuth } from './AppUnderTest';
import apiService from './apiService';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    company: user?.company || '',
    phone: user?.phone || '',
    role: user?.role || '',
    bio: user?.bio || ''
  });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setSaved(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await apiService.updateProfile(formData);

      if (response.success) {
        updateUser(response.user);
        setSaved(true);
        // Clear saved message after 3 seconds
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="aut-profile">
      <div className="aut-page-header">
        <h1 className="aut-page-title">Profile</h1>
        <p className="aut-page-description">Manage your personal information and preferences.</p>
      </div>

      {saved && (
        <div className="aut-alert aut-alert-success" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Check size={18} />
          <span>Your profile has been updated successfully.</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '32px' }}>
        {/* Profile Form */}
        <div className="aut-card">
          <div className="aut-card-header">
            <h2 className="aut-card-title">Personal Information</h2>
          </div>

          <form className="aut-form" onSubmit={handleSubmit}>
            <div className="aut-form-row">
              <div className="aut-form-group">
                <label className="aut-label" htmlFor="name">Full Name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  className="aut-input"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>

              <div className="aut-form-group">
                <label className="aut-label" htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  className="aut-input"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="aut-form-row">
              <div className="aut-form-group">
                <label className="aut-label" htmlFor="company">Company</label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  className="aut-input"
                  placeholder="Acme Corp"
                  value={formData.company}
                  onChange={handleChange}
                />
              </div>

              <div className="aut-form-group">
                <label className="aut-label" htmlFor="phone">Phone Number</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  className="aut-input"
                  placeholder="+1 555-0123"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="aut-form-group">
              <label className="aut-label" htmlFor="role">Role</label>
              <select
                id="role"
                name="role"
                className="aut-select"
                value={formData.role}
                onChange={handleChange}
              >
                <option value="">Select a role</option>
                <option value="Developer">Developer</option>
                <option value="Designer">Designer</option>
                <option value="Project Manager">Project Manager</option>
                <option value="QA Engineer">QA Engineer</option>
                <option value="DevOps Engineer">DevOps Engineer</option>
                <option value="Product Manager">Product Manager</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="aut-form-group">
              <label className="aut-label" htmlFor="bio">Bio</label>
              <textarea
                id="bio"
                name="bio"
                className="aut-textarea"
                placeholder="Tell us about yourself..."
                value={formData.bio}
                onChange={handleChange}
                rows={4}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
              <button type="button" className="aut-btn aut-btn-secondary" onClick={() => setFormData({
                name: user?.name || '',
                email: user?.email || '',
                company: user?.company || '',
                phone: user?.phone || '',
                role: user?.role || '',
                bio: user?.bio || ''
              })}>
                Cancel
              </button>
              <button type="submit" className="aut-btn aut-btn-primary" disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>

        {/* Profile Preview */}
        <div>
          <div className="aut-card" style={{ textAlign: 'center' }}>
            <div className="aut-avatar" style={{
              width: '80px',
              height: '80px',
              fontSize: '32px',
              margin: '0 auto 20px'
            }}>
              {formData.name ? formData.name.charAt(0) : <User size={32} />}
            </div>
            <h3 style={{ margin: '0 0 4px 0', fontWeight: 600 }}>{formData.name || 'Your Name'}</h3>
            <p style={{ color: 'var(--aut-text-secondary)', margin: '0 0 4px 0' }}>{formData.role || 'Your Role'}</p>
            <p style={{ color: 'var(--aut-text-muted)', fontSize: '14px', margin: 0 }}>{formData.company || 'Your Company'}</p>
          </div>

          <div className="aut-card" style={{ marginTop: '16px' }}>
            <h3 className="aut-detail-section-title">Account Info</h3>
            <div className="aut-detail-field">
              <div className="aut-detail-label">Member Since</div>
              <div className="aut-detail-value">November 2024</div>
            </div>
            <div className="aut-detail-field">
              <div className="aut-detail-label">Plan</div>
              <span className="aut-badge aut-badge-active">Professional</span>
            </div>
            <div className="aut-detail-field">
              <div className="aut-detail-label">Projects</div>
              <div className="aut-detail-value">12</div>
            </div>
          </div>
        </div>
      </div>

      {/* Password Change Section */}
      <div className="aut-card" style={{ marginTop: '32px' }}>
        <div className="aut-card-header">
          <h2 className="aut-card-title">Change Password</h2>
        </div>

        <form className="aut-form" onSubmit={(e) => { e.preventDefault(); alert('Password change functionality coming soon!'); }}>
          <div className="aut-form-group" style={{ maxWidth: '400px' }}>
            <label className="aut-label" htmlFor="currentPassword">Current Password</label>
            <input
              id="currentPassword"
              name="currentPassword"
              type="password"
              className="aut-input"
              placeholder="Enter current password"
            />
          </div>

          <div className="aut-form-row" style={{ maxWidth: '816px' }}>
            <div className="aut-form-group">
              <label className="aut-label" htmlFor="newPassword">New Password</label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                className="aut-input"
                placeholder="Enter new password"
              />
            </div>

            <div className="aut-form-group">
              <label className="aut-label" htmlFor="confirmNewPassword">Confirm New Password</label>
              <input
                id="confirmNewPassword"
                name="confirmNewPassword"
                type="password"
                className="aut-input"
                placeholder="Confirm new password"
              />
            </div>
          </div>

          <div style={{ marginTop: '8px' }}>
            <button type="submit" className="aut-btn aut-btn-secondary">
              Update Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile;
